import {
  createMemo,
  useContext,
  createContext,
  createSignal,
  createEffect,
  createSelector,
  createUniqueId,
  splitProps,
  Show,
  batch,
  For,
  Switch,
  Match,
  Suspense,
} from "https://cdn.jsdelivr.net/npm/solid-js@1.9.5/+esm";
import { render } from "https://cdn.jsdelivr.net/npm/solid-js@1.9.5/web/+esm";
import html from "https://cdn.jsdelivr.net/npm/solid-js@1.9.5/html/+esm";
import {
  HashRouter,
  createAsync,
} from "https://cdn.jsdelivr.net/npm/@brytdesigns/fork-solid-router@0.15.4/+esm";

import StorefrontCart from "https://cdn.jsdelivr.net/npm/@bryt-designs/storefront-cart@2.2.41";
import StorefrontClient from "https://cdn.jsdelivr.net/npm/@bryt-designs/storefront-client@0.7.7";
import uniqBy from "https://cdn.jsdelivr.net/npm/lodash.uniqby@4.7.0/+esm";
import differenceBy from "https://cdn.jsdelivr.net/npm/lodash.differenceby@4.8.0/+esm";

const client = StorefrontClient();

const uniq = (arr) => [...new Set(arr)];

const formatId = (type) => (id) => `gid://shopify/${type}/${id}`;
const parseId = (gid) => gid.split("/").pop();
const toMetaobjectGID = formatId("Metaobject");
const toProductGID = formatId("Product");

const IMAGE_FRAGMENT = `#graphql
  fragment ImageFragment on MediaImage {
    details: image {
      alt: altText
      width
      height
      square: url(transform:{ maxWidth: 100, maxHeight:100 })
      src: url(transform:{ maxWidth: 500 })
    }
  }
`;

const INGREDIENT_FRAGMENT = `#graphql
  fragment IngredientFragment on Metaobject {
      id
      name: field(key: "name") {
        value
      }
      info: field(key: "short_info") {
        value
      }
      image: field(key: "image") {
        id: value
        data: reference {
          ...ImageFragment
        }
      }
      description: field(key: "information") {
        value
      }
      why: field(key: "why") {
        value
      }
  }
  ${IMAGE_FRAGMENT}
`;

const VARIANT_FRAGMENT = `#graphql
  fragment VariantFragment on ProductVariant {
    id
    title
    price {
      amount
    }
    compareAtPrice {
      amount
    }
    isHidden: metafield(namespace: "general", key: "hidden") {
      value
    }
    shortTitle: metafield(namespace: "general", key: "variant_title") {
      value
    }
  }
`;

const PRODUCT_FRAGMENT = `#graphql
  fragment ProductFragment on Product {
    id
    title
    description
    url: onlineStoreUrl
    featuredImage {
      id
      alt: altText
      width
      height
      src: url(transform:{ maxWidth: 500 })
    }
    oosMessaging: metafield(namespace: "general", key: "oos_messaging") {
      value
    }
    shortenedTitle: metafield(namespace: "general", key: "shortened_title") {
      value
    }
    recommenedImage: metafield(namespace: "quiz", key: "featured_image") {
      value
      data: reference {
        ...ImageFragment
      }
    }
    variants(first: 50) {
      nodes {
        ...VariantFragment
      }
    }
  }
  ${VARIANT_FRAGMENT}
`;

const OUTCOME_DATA_QUERY = `#graphql
  query OutcomeData($ingredientIds: [ID!]!, $productIds: [ID!]!) {
    ingredients: nodes(ids: $ingredientIds) {
      ...IngredientFragment
    }
    products: nodes(ids:$productIds) {
    	...ProductFragment
    }
  }

  ${PRODUCT_FRAGMENT}
  ${INGREDIENT_FRAGMENT}
`;

const INGREDIENT = {
  KELP: 79363899574,
  GINSENG: 79364489398,
  CAFFEINE: 79364358326,
  L_THEANINE: 93510041782,
  MAGNESIUM: 101448417462,
  LEMON_BALM: 93508894902,
  BETA_GLUCAN: 77709017270,
  PREBIOTICS: 77709443254,
  PROBIOTICS: 77709476022,
  ZINC: 77708787894,
  TURMERIC: 77708624054,
  VITAMIN_K2: 77709050038,
  CHLORELLA: 77709803702,
  VITAMIN_B: 77709574326,
  VALERIAN_ROOT: 82435932342,
  VITAMIN_D: 77708689590,
};

const PRODUCT = {
  ENERGY_AID: 8275669811382,
  CHILL_OUT: 8275669483702,
  GEM_BITE: 8275664273590,
  DEEP_SLEEP: 8275668598966,
};

const LIFESTYLE_COPY = {
  "lifestyle.live.city": "live in a <strong>city</strong>",
  "lifestyle.servings.1-2":
    "consume <strong>2 or fewer fruits & veggies per day</strong>",
  "lifestyle.servings.none":
    "consume <strong>2 or fewer fruits & veggies per day</strong>",
  "lifestyle.sunshine.little": "get <strong>little sunshine</strong>",
  "lifestyle.travel.once-per-month": "are a <strong>frequent traveler</strong>",
};

// const GOAL = {
//   SLEEP: "Sleep",
//   MOOD_STRESS: "Mood + stress",
//   HAIR_SKIN_NAILS: "Hair, Skin, & Nails",
//   BRAIN_COGNITION: "Brain & Cognition",
//   FITNESS: "Fitness",
//   ENERGY: "Energy",
//   IMMUNITY: "Immunity",
//   BONE_JOINT: "Bone + joint health",
//   GUT_HEALTH: "Gut health",
//   METABOLISM: "Metabolism & Weight Loss",
// };

const RESULTS_OUTCOMES = [
  {
    answers: [
      "goals.energy.concerns.focus-clarity",
      "goals.energy.concerns.metabolic",
      "goals.energy.concerns.physical",
      "goals.energy.concerns.general",
      "goals.fitness.top_priority.preworkout-snack",
      "goals.fitness.top_priority.stamina-endurance",
      "goals.fitness.top_priority.weightloss",
      "goals.fitness.top_priority.other",
    ],
    ingredients: [INGREDIENT.KELP, INGREDIENT.GINSENG, INGREDIENT.CAFFEINE].map(
      toMetaobjectGID,
    ),
    ingredientCopy:
      "Boosts metabolic energy, focus, and stamina while reducing fatigue.",
    product: toProductGID(PRODUCT.ENERGY_AID),
  },
  {
    answers: [
      "goals.fitness.top_priority.preworkout-snack",
      "goals.brain-cognition.concerns.energy",
      "goals.brain-cognition.concerns.cognition",
    ],
    ingredients: [INGREDIENT.L_THEANINE, INGREDIENT.GINSENG].map(
      toMetaobjectGID,
    ),
    ingredientCopy:
      "Enhances cognitive function, focus, and energy while reducing energy crashes.",
    product: toProductGID(PRODUCT.ENERGY_AID),
  },
  {
    answers: [
      "goals.fitness.top_priority.recovery-support",
      "goals.gut-health.concerns.metabolism",
      "goals.gut-health.concerns.sugar-cravings",
    ],
    ingredients: [INGREDIENT.GINSENG, INGREDIENT.KELP].map(toMetaobjectGID),
    ingredientCopy:
      "Supports faster metabolism, gut health, and fitness recovery.",
    product: toProductGID(PRODUCT.ENERGY_AID),
  },
  {
    answers: [
      "goals.mood-stress.stress_affect.digestion",
      "goals.mood-stress.stress_affect.anxiety",
      "goals.mood-stress.stress_affect.inflammation",
      "goals.mood-stress.stress_affect.mood",
      "goals.mood-stress.stress_affect.other",
      "goals.energy.concerns.emotional",
      "goals.metabolism.concerns.general-digestion",
    ],
    ingredients: [INGREDIENT.MAGNESIUM, INGREDIENT.LEMON_BALM].map(
      toMetaobjectGID,
    ),
    ingredientCopy:
      "Reduces stress, anxiety, and inflammation while supporting digestion and emotional energy.",
    product: toProductGID(PRODUCT.CHILL_OUT),
  },
  {
    answers: [
      "goals.importance.immunity",
      "goals.importance.bone-joint",
      "goals.mood-stress.stress_affect.digestion",
      "goals.mood-stress.stress_affect.anxiety",
      "goals.mood-stress.stress_affect.inflammation",
      "goals.mood-stress.stress_affect.mood",
      "goals.mood-stress.stress_affect.other",
      "goals.mood-stress.stress_affect.no-sleep",
      "goals.mood-stress.stress_affect.all-above",
      "lifestyle.sunshine.little",
    ],
    ingredients: [INGREDIENT.VITAMIN_D].map(toMetaobjectGID),
    ingredientCopy:
      "Strengthens immunity, supports bone and joint health, and improves overall mood. Also good if you don't get enough sunshine!",
    product: toProductGID(PRODUCT.GEM_BITE),
  },
  {
    answers: [
      "goals.importance.immunity",
      "goals.importance.gut-health",
      "goals.fitness.top_priority.weightloss",
      "goals.metabolism.concerns.appetite-satiation",
    ],
    ingredients: [INGREDIENT.BETA_GLUCAN].map(toMetaobjectGID),
    ingredientCopy:
      "Boosts immune function and may help boost levels of GLP-1. Supports overall gut health.",
    product: toProductGID(PRODUCT.GEM_BITE),
  },
  {
    answers: [
      "goals.gut-health.concerns.digestion",
      "goals.gut-health.concerns.regularity",
      "lifestyle.servings.1-2",
      "lifestyle.servings.none",
    ],
    ingredients: [INGREDIENT.PREBIOTICS, INGREDIENT.PROBIOTICS].map(
      toMetaobjectGID,
    ),
    ingredientCopy:
      "Promotes gut health, digestion, and metabolism, especially for those with low fruit and vegetable intake.",
    product: toProductGID(PRODUCT.GEM_BITE),
  },
  {
    answers: ["goals.importance.immunity", "lifestyle.travel.once-per-month"],
    ingredients: [
      INGREDIENT.ZINC,
      INGREDIENT.BETA_GLUCAN,
      INGREDIENT.VITAMIN_D,
      INGREDIENT.TURMERIC,
    ].map(toMetaobjectGID),
    ingredientCopy:
      "Strengthens the immune system, making it ideal for frequent travelers.",
    product: toProductGID(PRODUCT.GEM_BITE),
  },
  {
    answers: ["goals.importance.bone-joint"],
    ingredients: [INGREDIENT.VITAMIN_K2].map(toMetaobjectGID),
    ingredientCopy: "Supports strong bones and joint health.",
    product: toProductGID(PRODUCT.GEM_BITE),
  },
  {
    answers: ["goals.importance.hair-skin-nails"],
    ingredients: [
      INGREDIENT.VITAMIN_B,
      INGREDIENT.TURMERIC,
      INGREDIENT.VITAMIN_K2,
      INGREDIENT.VITAMIN_D,
    ].map(toMetaobjectGID),
    ingredientCopy: "Enhances hair, skin, and nail health.",
    product: toProductGID(PRODUCT.GEM_BITE),
  },
  {
    answers: ["lifestyle.live.city"],
    ingredients: [INGREDIENT.CHLORELLA].map(toMetaobjectGID),
    ingredientCopy:
      "Provides essential nutrients for city dwellers, supporting overall wellness and detoxifcation from heavy metals and pollutants",
    product: toProductGID(PRODUCT.GEM_BITE),
  },
  {
    answers: [
      "goals.brain-cognition.concerns.memory",
      "goals.brain-cognition.concerns.cognition",
      "goals.brain-cognition.concerns.focus",
      "goals.brain-cognition.concerns.energy",
      "goals.brain-cognition.concerns.other",
    ],
    ingredients: [INGREDIENT.VITAMIN_B].map(toMetaobjectGID),
    ingredientCopy: "Improves brain function, cognition, energy, and focus.",
    product: toProductGID(PRODUCT.GEM_BITE),
  },
  {
    answers: ["goals.importance.sleep"],
    ingredients: [
      INGREDIENT.MAGNESIUM,
      INGREDIENT.VALERIAN_ROOT,
      INGREDIENT.L_THEANINE,
    ].map(toMetaobjectGID),
    ingredientCopy:
      "Aids in relaxation and sleep support, particularly for those struggling with insomnia.",
    product: toProductGID(PRODUCT.GEM_BITE),
  },
  {
    answers: [
      "goals.gut-health.concerns.inflammation",
      "goals.gut-health.concerns.bloating",
    ],
    ingredients: [INGREDIENT.TURMERIC].map(toMetaobjectGID),
    ingredientCopy:
      "Reduces inflammation and bloating while promoting gut health.",
    product: toProductGID(PRODUCT.DEEP_SLEEP),
  },
];

const MetaContext = createContext(null);

function useMeta() {
  const context = useContext(MetaContext);
  if (!context)
    throw new Error("BundleContext must be used within a BundleProvider");
  return context;
}

function getMeta() {
  const url = new URL(import.meta.url);
  const script = document.querySelector(
    `script[src*="${url.pathname + url.search}"]`,
  );
  if (!script)
    return console.warn(
      `Unable to find the script tag with the provided URL. Skipping script execution.`,
    );
  const sectionId = script.getAttribute("data-section-id");
  if (!sectionId)
    return console.warn(
      `No section ID provided (data-section-id). Skipping script execution.`,
    );
  const section = document.getElementById(`shopify-section-${sectionId}`);
  if (!section)
    return console.warn(
      `Unable to find the section with the provided ID. Skipping script execution.`,
    );

  const metaData = () => {
    try {
      const script = section.querySelector('script[type="application/json"]');
      if (!script) return null;
      return JSON.parse(script.textContent);
    } catch (error) {
      console.warn(error);
      return null;
    }
  };
  return metaData;
}

const ProductContext = createContext(null);
const PriveContext = createContext(null);

function useProduct() {
  const context = useContext(ProductContext);
  if (!context)
    throw new Error("ProductContext must be used within a ProductProvider");
  return context;
}
function ProductProvider(props) {
  const variants = () =>
    props.product.variants.nodes.filter((v) => !v.isHidden?.value);
  const inCartItemVariantId = createMemo(() => {
    const p = props.product;
    return StorefrontCart.cart().items.find(
      (item) => item.product_id === parseId(p.id),
    )?.variant_id;
  });
  const [selectedVariant, setSelectedVariant] = createSignal(
    variants()?.find((v) => parseId(v.id) === `${inCartItemVariantId()}`) ||
      variants()?.at(0) ||
      null,
  );

  const product = createMemo(() => {
    return {
      ...props.product,
      variants: variants(),
      get selectedVariant() {
        return selectedVariant();
      },
    };
  });

  const context = [
    product,
    {
      variant: {
        update: setSelectedVariant,
      },
    },
  ];

  return html`
    <${ProductContext.Provider} value=${context}>
      <${PriveProvider}> ${() => props.children} <//>
    <//>
  `;
}

function createWidgetProductSellingPlansUrl({ storeUrl, productId }) {
  return `https://subs.api.tryprive.com/stores/${storeUrl}/products/${productId}/new`;
}

function createWidgetSettingsUrl(storeUrl) {
  return `https://subs.api.tryprive.com/stores/${storeUrl}/settings`;
}

async function fetchWidgetSettings(storeUrl) {
  const endpoint = createWidgetSettingsUrl(storeUrl);
  const response = await fetch(endpoint, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

async function fetchProductSellingPlans({ storeUrl, productId }) {
  const endpoint = createWidgetProductSellingPlansUrl({ storeUrl, productId });
  const response = await fetch(endpoint, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

function extractPeriod(input) {
  const lower = input.toLowerCase();
  if (lower.includes("week")) return "WEEK";
  if (lower.includes("month")) return "MONTH";
  if (lower.includes("day") || lower.includes("daily")) return "DAY";
  return "DAY";
}

function getSelectedSellingPlanByVariant({ variant, sellingPlans }) {
  if (!variant) return null;
  if (!sellingPlans?.length) return null;
  const variantCadenceCount = parseInt(variant.title);
  const variantCadenceUnit = extractPeriod(variant.title);
  const sellingPlan = sellingPlans.find((plan) => {
    return (
      plan.deliveryCadenceCount === variantCadenceCount &&
      plan.deliveryCadenceUnit === variantCadenceUnit
    );
  });
  return sellingPlan;
}

function calcSellingPlanPrice({ variant, sellingPlan }) {
  const discount = sellingPlan?.discount;
  const price = variant.price;
  if (!discount || !sellingPlan) return price;
  let discountedPrice;
  if (sellingPlan?.discountModifier === "PERCENTAGE") {
    discountedPrice = price - price * (discount / 100);
  } else {
    discountedPrice = price - discount;
  }
  return discountedPrice;
}

function usePrive() {
  const context = useContext(PriveContext);
  if (!context)
    throw new Error("PriveContext must be used within a PriveProvider");
  return context;
}

function PriveProvider(props) {
  const [product, _] = useProduct();
  const meta = useMeta();
  const data = createAsync(async () => {
    const storeUrl = meta?.permanent_domain;
    if (!storeUrl) return null;
    const p = product();
    if (!p) return null;
    const [settings, options] = await Promise.all([
      fetchWidgetSettings(storeUrl),
      fetchProductSellingPlans({ storeUrl, productId: parseId(p.id) }),
    ]);

    return {
      settings,
      options,
    };
  });

  const inCartItem = createMemo(() => {
    const p = product();
    return StorefrontCart.cart().items.find(
      (item) => item.product_id === parseId(p.id),
    );
  });

  const [purchaseOption, setPurchaseOption] = createSignal(
    inCartItem() ? (inCartItem().selling_plan?.id ? "sub" : "one") : "sub",
  );

  const settings = () => data()?.settings?.widget_settings;
  const sellingPlanGroups = () => data()?.options?.selling_plan_groups;
  const variant = () => product()?.selectedVariant;

  const sellingPlans = createMemo(() => {
    return sellingPlanGroups()?.flatMap((group) => group.selling_plans) ?? [];
  });

  const selectedSellingPlan = createMemo(() => {
    const plans = sellingPlans();
    const selectedVariant = variant();
    return getSelectedSellingPlanByVariant({
      variant: selectedVariant,
      sellingPlans: plans,
    });
  });

  const sellingPlanAmount = createMemo(() => {
    const sellingPlan = selectedSellingPlan();
    return calcSellingPlanPrice({ variant: variant(), sellingPlan });
  });

  const sellingPlanDiscountPercentage = createMemo(() => {
    const sellingPlanPrice = sellingPlanAmount();
    const price = variant().price;
    return 100 - Math.round((sellingPlanPrice / price) * 100);
  });

  const context = [
    {
      get purchaseOption() {
        return purchaseOption();
      },
      widget: {
        get data() {
          return data();
        },
        get settings() {
          return settings();
        },
        get sellingPlanGroups() {
          return sellingPlanGroups();
        },
      },
      sellingPlan: {
        get plans() {
          return sellingPlans();
        },
        get selected() {
          return selectedSellingPlan();
        },
        get price() {
          return sellingPlanAmount();
        },
        get discountPercentage() {
          return sellingPlanDiscountPercentage();
        },
      },
    },
    {
      purchaseOption: {
        update: setPurchaseOption,
      },
    },
  ];

  return html`
    <${PriveContext.Provider} value=${context}> ${() => props.children} <//>
  `;
}

function MetaProvider(props) {
  const metaData = getMeta();

  return html`
    <${MetaContext.Provider} value=${metaData()}> ${() => props.children} <//>
  `;
}

const getResults = () => {
  const results = localStorage.getItem("2025-quiz-results");
  if (!results) {
    const meta = getMeta();
    window.location.href = `${window.location.origin}${meta()?.quizUrl}${window.location.search}`;
  }
  return results ? JSON.parse(results) : {};
};

const ResultsContext = createContext(null);

function useResults() {
  const context = useContext(ResultsContext);
  if (!context)
    throw new Error("ResultsContext must be used within a ResultsProvider");
  return context;
}

function getValidOutcomes(results, outcomes) {
  function getValueByPath(obj, path) {
    return path.split(".").reduce((acc, key) => {
      return acc && acc[key] ? acc[key] : undefined;
    }, obj);
  }

  return outcomes.filter((outcome) =>
    outcome.answers.some((answerPath) => {
      let pathSegments = answerPath.split(".");
      let key = pathSegments.pop();
      let parentPath = pathSegments.join(".");
      let parent = getValueByPath(results, parentPath);

      return (
        parent &&
        Array.isArray(parent.values) &&
        parent.values.some((valueObj) => valueObj.value === key)
      );
    }),
  );
}

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function resolveQuestionPaths(results, paths) {
  function getValueByPath(obj, path) {
    return path
      .split(".")
      .reduce((acc, key) => (acc && acc[key] ? acc[key] : undefined), obj);
  }

  return paths.reduce((acc, path) => {
    let pathSegments = path.split(".");
    let key = pathSegments.pop();
    let parentPath = pathSegments.join(".");
    let parent = getValueByPath(results, parentPath);

    if (
      parent &&
      Array.isArray(parent.values) &&
      parent.values.some((valueObj) => valueObj.value === key)
    ) {
      acc.push({
        answer: path,
        question: parent.question,
        values: parent.values,
      });
    }

    return acc;
  }, []);
}

function mapIdsToObjects(ids, dataset) {
  const idMap = dataset.reduce((acc, obj) => {
    acc[obj.id] = obj;
    return acc;
  }, {});

  return ids.map((id) => idMap[id]).filter((obj) => obj !== undefined);
}

function replaceLastComma(str, replacement = "") {
  const lastCommaIndex = str.lastIndexOf(",");
  if (lastCommaIndex === -1) return str; // No comma found, return original string

  return (
    str.slice(0, lastCommaIndex) + replacement + str.slice(lastCommaIndex + 1)
  );
}

function extractAnswers(obj) {
  let answers = [];

  function traverse(node) {
    if (typeof node !== "object" || node === null) {
      return;
    }

    if (node.hasOwnProperty("question") && node.hasOwnProperty("values")) {
      answers.push({
        question: node.question,
        values: node.values,
      });
      return;
    }

    for (const key in node) {
      if (node.hasOwnProperty(key)) {
        traverse(node[key]);
      }
    }
  }

  traverse(obj);
  return answers;
}

function ResultsProvider(props) {
  const results = getResults();
  const validOutcomes = getValidOutcomes(results, RESULTS_OUTCOMES);
  const ingredientIds = uniq(
    validOutcomes.flatMap((outcome) => outcome.ingredients),
  );
  const productIds = uniq(Object.values(PRODUCT).map(toProductGID));

  const outcomeData = createAsync(async () => {
    const req = await client.query({
      query: OUTCOME_DATA_QUERY,
      variables: {
        ingredientIds,
        productIds,
      },
    });

    const products = req.data.products.map((product) => ({
      ...product,
      variants: {
        nodes: product.variants.nodes.map((variant) => ({
          ...variant,
          price: variant.price.amount * 100,
          compareAtPrice: variant.compareAtPrice
            ? variant.compareAtPrice.amount * 100
            : null,
        })),
      },
    }));

    const populatedOutcomes = validOutcomes.map((outcome) => ({
      answers: resolveQuestionPaths(results, outcome.answers),
      ingredients: mapIdsToObjects(outcome.ingredients, req.data.ingredients),
      product: mapIdsToObjects([outcome.product], products)?.at(0),
      ingredientCopy: outcome.ingredientCopy,
    }));

    const recommendedProducts = uniqBy(
      populatedOutcomes.map((outcome) => outcome.product),
      "id",
    ).map((product) => {
      const outcomes = populatedOutcomes.filter(
        (outcome) => outcome.product.id === product.id,
      );
      return {
        ...product,
        answers: uniqBy(
          outcomes.flatMap((outcome) => outcome.answers),
          "answer",
        ),
      };
    });

    const result = {
      matchedAnswers: uniqBy(
        populatedOutcomes.flatMap((outcome) => outcome.answers),
        "answer",
      ),
      populated: populatedOutcomes,
      // Max 3 recommended products, and the rest in the "Other Products" section
      recommendedProducts: recommendedProducts.slice(0, 3),
      original: {
        answers: results,
        outcomes: validOutcomes,
      },
      otherProducts: differenceBy(
        products,
        recommendedProducts.slice(0, 3),
        "id",
      ),
    };

    await fetch("/cart.js");

    // await wait(2000);

    return result;
  });

  return html`
    <${ResultsContext.Provider} value=${() => outcomeData}>
      ${() => props.children}
    <//>
  `;
}

function LoadingResults() {
  return html`
    <div
      class="flex justify-center w-full overflow-hidden min-h-screen py-24 text-center"
    >
      <div class="container flex-1">
        <div class="flex flex-col justify-between h-full">
          <div class="max-w-[365px] mx-auto">
            <h2 class="mb-0 text-6xl">18%</h2>
            <p class="text-base [&_a]:underline">
              Generating your personalized results...
            </p>
            <div class="aspect-1 before:float-left before:pt-[100%] after:block after:clear-both">
              <img
                class="object-cover w-full h-full mb-6 rounded-md"
                src="https://cdn.shopify.com/s/files/1/0002/5673/7286/files/231018_GEM06_Sleep_Angle-0562_231103_v2_Shadow_1.png?v=1740603139"
                alt=""
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
}

function stripHtmlAndContent(input) {
  return input.replace(/<[^>]+>.*?<\/[^>]+>/gs, ""); // Remove full tag pairs and content
}

function useAllLifestyles() {
  const results = useResults();

  const lifestyles = createMemo(() => {
    const lifestyleAnswers = results()?.matchedAnswers.filter((a) =>
      a.answer.includes("lifestyle"),
    );
    return lifestyleAnswers.map((value) => LIFESTYLE_COPY[value.answer]);
  });

  return lifestyles;
}

function useAllGoals() {
  const results = useResults();

  const goals = createMemo(() => {
    const goalAnswers = results()?.matchedAnswers.filter((a) =>
      a.answer.includes("goals"),
    );
    return goalAnswers.reduce((chosenGoals, answer) => {
      const goalName = answer.answer.split(".")?.at(-1);
      const goal = answer.values.find((v) => v.value === goalName);
      if (goal) {
        chosenGoals.push(stripHtmlAndContent(goal.label));
      }
      return chosenGoals;
    }, []);
  });

  return goals;
}

function useGoalsByProduct(product) {
  const productGoals = createMemo(() => {
    const p = product();

    const goalAnswers = p.answers.filter((a) => a.answer.includes("goals"));

    return goalAnswers.reduce((chosenGoals, answer) => {
      const goalName = answer.answer.split(".")?.at(-1);
      const goal = answer.values.find((v) => v.value === goalName);
      if (goal) {
        chosenGoals.push(stripHtmlAndContent(goal.label));
      }
      return chosenGoals;
    }, []);
  });

  return productGoals;
}

function useIngredientsByProduct(product) {
  const results = useResults();

  const ingredients = createMemo(() => {
    const p = product();

    const populatedProduct = results()?.populated?.filter(
      (o) => o.product.id === p.id,
    );

    return uniqBy(
      populatedProduct.flatMap((p) => p.ingredients),
      "id",
    );
  });

  return ingredients;
}

function AllGoals() {
  const goals = useAllGoals();

  return () => {
    const joinedGoals = replaceLastComma(
      goals()
        .map((goal) => `<strong>${goal.toLowerCase()}</strong>`)
        .join(", "),
      ", and",
    );
    return html`<span innerHTML=${joinedGoals} />`;
  };
}

function LifestyleCopy() {
  const lifestyles = useAllLifestyles();

  return () => {
    const joinedLifestyles = replaceLastComma(
      lifestyles()
        .map((lifestyle) => `<span>${lifestyle}</span>`)
        .join(", "),
      ", and",
    );
    return html`<span innerHTML=${joinedLifestyles} />`;
  };
}

function ProductGoals(props) {
  const goals = useGoalsByProduct(() => props.product);

  return () => {
    const joinedGoals = replaceLastComma(
      goals()
        .map((goal) => `<strong>${goal.toLowerCase()}</strong>`)
        .join(", "),
      ", and",
    );
    return html`<span innerHTML=${joinedGoals} />`;
  };
}

function IngredientsModal(props) {
  return html`
    <modal-context
      id=${() => props.id}
      aria-label="Open"
      role="dialog"
      aria-modal="true"
      is-animating="false"
      style="display: none"
      class="relative z-[10000] group hidden"
      trap-focus="false"
    >
      <modal-backdrop
        class="fixed inset-0 bg-black bg-opacity-25 cursor-pointer backdrop-blur-sm [--opacity-from:0] [--opacity-to:1]"
      ></modal-backdrop>
      <modal-panel
        class="mobile:p-0 fixed inset-0 overflow-auto pointer-events-auto mx-auto flex min-h-full w-full max-w-4xl items-center justify-center p-4 text-left [--opacity-from:0] [--opacity-to:1]"
      >
        <div class="h-full w-full">
          <div class="h-max flex w-full">
            <div class="container flex items-center justify-center">
              <div class="w-full rounded-md relative" tabindex="0">
                <div
                  class="flex justify-between items-center rounded-t-md bg-section-background px-3 z-10 py-5"
                >
                  <h3>Ingredients</h3>
                  <modal-trigger
                    target=${() => props.id}
                    action="close"
                    on="click"
                  >
                    <button class="cursor-pointer">
                      <svg
                        class="w-5 h-auto"
                        xmlns="http://www.w3.org/2000/svg"
                        width="25.467"
                        height="25.467"
                        viewBox="0 0 25.467 25.467"
                      >
                        <g transform="translate(2.828 2.828)">
                          <line
                            x1="19.81"
                            y1="19.81"
                            fill="none"
                            stroke="#301D01"
                            stroke-linecap="round"
                            stroke-miterlimit="10"
                            stroke-width="4"
                          />
                          <line
                            y1="19.81"
                            x2="19.81"
                            fill="none"
                            stroke="#301D01"
                            stroke-linecap="round"
                            stroke-miterlimit="10"
                            stroke-width="4"
                          />
                        </g>
                      </svg>
                    </button>
                  </modal-trigger>
                </div>

                <${For} each=${() => props.ingredients}>
                  ${(ingredient) => html`
                    <${Show}
                      when=${() =>
                        ingredient.description?.value &&
                        ingredient.image?.data?.details?.src &&
                        ingredient.name?.value &&
                        ingredient.why?.value}
                    >
                      <div
                        class="group relative border-t border-section-heading/20 block last-of-type:border-b bg-no-repeat bg-cover last:rounded-b-xl"
                        style=${() =>
                          `background-image: url('${ingredient.image.data.details.src}');`}
                      >
                        <div
                          class="absolute z-[1] top-0 right-0 left-0 bottom-0 bg-black opacity-35 pointer-events-none rounded-b-xl"
                        ></div>
                        <div class="relative z-[5] p-6">
                          <h3
                            class="relative py-[15px] mb-0 flex gap-4 items-center justify-between text-white"
                          >
                            ${() => ingredient.name.value}
                          </h3>
                          <p
                            class="prose transition-opacity py-4 mb-2 text-lg text-white uppercase"
                          >
                            WHY IT'S IN GEM: ${() => ingredient.why.value}
                          </p>
                          <p
                            class="prose font-body transition-opacity py-4 mb-2 text-lg text-white"
                          >
                            ${() => ingredient.description.value}
                          </p>
                        </div>
                      </div>
                    <//>
                  `}
                <//>
              </div>
            </div>
          </div>
        </div>
      </modal-panel>
    </modal-context>
  `;
}

function CustomRadio(props) {
  return html`
    <div class="relative h-max">
      <span
        class="border border-current h-[1.25em] w-[1.25em] rounded bg-white block transition-colors group-peer-checked:bg-current"
      ></span>
      <span
        class="block absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 w-[.5em] h-[.5em] rounded-full bg-transparent scale-75 transition-all group-peer-checked:bg-white group-peer-checked:scale-100"
      ></span>
    </div>
  `;
}

function GroupOptionLabel(props) {
  const [component, rest] = splitProps(props, ["label", "children"]);
  return html`
    <label
      ...${rest}
      class="border rounded border-section-body p-3.5 block text-sm font-bold peer-checked:bg-section-accent-one transition-colors gap-2 group text-section-body cursor-pointer flex gap-2 font-[Poppins,_sans-serif] peer-disabled:opacity-60 peer-disabled:cursor-not-allowed"
    >
      <${CustomRadio} />
      ${() => component.label}
    </label>
    ${() => component.children}
  `;
}

function PriveVariantWidget(props) {
  const [product] = useProduct();
  const [state, methods] = usePrive();
  const variant = () => product().selectedVariant;

  const productId = createMemo(() => parseId(product().id));

  const firstSellingPlan = () =>
    state.widget.sellingPlanGroups?.at(0)?.selling_plans?.at(0);

  const formattedSellingPlanAmount = createMemo(() => {
    const sellingPlan = state.sellingPlan.selected;
    const discount = state.sellingPlan.discountPercentage;
    const price = variant().price;
    if (!discount || !sellingPlan) return Shopify.formatMoney(price);
    return Shopify.formatMoney(state.sellingPlan.price);
  });

  const otpAllowed = () =>
    state.widget.sellingPlanGroups?.some((group) =>
      group.type.includes("ONETIME"),
    );

  const hasSellingPlans = () => state.widget.sellingPlanGroups?.length > 0;

  const otpIsFirst = () => state.widget.settings?.onetimeFirst;

  const hasShippingDiscount = () =>
    !!state.sellingPlan.selected.shippingDiscount;

  const hasDiscount = () => !!state.sellingPlan.selected.discount;

  function handleChange(e) {
    if (!props.onChange) return;
    props.onChange();
  }

  function OtpOption() {
    const id = () =>
      props.form
        ? `${props.form}-purchaseOption-${productId()}-one`
        : `purchaseOption-${productId()}-one`;

    const label = () => html`
      <span class="flex justify-between w-full">
        <span
          >${() =>
            state.widget.settings?.onetimeCopy || "One-time purchase:"}</span
        >
        <span>${() => Shopify.formatMoney(variant().price)}</span>
      </span>
    `;

    return html`
      <div>
        <input
          type="radio"
          form=${() => props.form || undefined}
          id=${id}
          name="purchase_option"
          class="sr-only peer"
          value="one"
          checked=${() => state.purchaseOption === "one"}
          onChange=${(e) => methods.purchaseOption.update("one")}
          disabled=${() => props.disabled}
        />
        <${GroupOptionLabel} for=${id} label=${label} />
      </div>
    `;
  }

  function SellingPlanOption() {
    const id = () =>
      props.form
        ? `${props.form}-purchaseOption-${productId()}-sub`
        : `purchaseOption-${productId()}-sub`;

    const defaultCopy = () =>
      hasDiscount() ? "Subscribe and Save:" : "Subscribe:";

    function formatSellingPlanPrice() {
      return Shopify.formatMoney(
        firstSellingPlan()?.shippingDiscount.requirements.amount * 100,
      );
    }

    const label = () => html`
      <span class="flex justify-between w-full">
        <span
          >${() => state.widget.settings?.subscribeCopy || defaultCopy()}</span
        >
        <span class="flex flex-col items-center relative">
          <${Show} when=${() => state.sellingPlan.discountPercentage > 0}>
            <span
              class="absolute -top-5 text-[10px] bg-section-accent-two rounded-full font-normal leading-none py-0.5 px-1 border border-current left-1/2 text-center min-w-14 -translate-x-1/2"
            >
              Save${" "}${() => state.sellingPlan.discountPercentage}%
            </span>
          <//>
          <span>${() => formattedSellingPlanAmount()}</span>
        </span>
      </span>
    `;

    const Label = () => html`
      <div class="flex flex-col w-full">
        ${label}
        <${Show} when=${() => hasShippingDiscount()}>
          <em class="text-xs font-normal">
            Free shipping on orders over ${" "}
            ${() => formatSellingPlanPrice()}
          </em>
        <//>
      </div>
    `;

    return html`
      <div>
        <input
          type="radio"
          form=${() => props.form || undefined}
          id=${id}
          name="purchase_option"
          class="sr-only peer"
          value="sub"
          disabled=${() => props.disabled}
          checked=${() => state.purchaseOption === "sub"}
          onChange=${(e) => methods.purchaseOption.update("sub")}
        />
        <${GroupOptionLabel} for=${id} label=${Label} />
      </div>
    `;
  }

  const id = () =>
    props.form
      ? `${props.form}-${productId()}-sellingPlan`
      : `purchaseOption-${productId()}-sellingPlan`;

  let isFirstRender = true;

  createEffect(() => {
    const _ = state.sellingPlan.selected?.sellingPlanId;
    const __ = state.purchaseOption;
    if (isFirstRender) return (isFirstRender = false);
    handleChange();
  });

  return html`
    <${Show} when=${() => state.widget.data}>
      <${Show} when=${hasSellingPlans}>
        <${Show} when=${() => !!state.sellingPlan.selected}>
          <div class="flex flex-col gap-2">
            <${Show}
              when=${() =>
                state.purchaseOption === "sub" && !!state.sellingPlan.selected}
            >
              <input
                type="hidden"
                form=${() => props.form || undefined}
                id=${id}
                name="selling_plan"
                class="sr-only"
                value=${() => state.sellingPlan.selected?.sellingPlanId}
                onChange=${(e) => handleChange(e)}
              />
            <//>
            <${Show} when=${() => otpIsFirst() && otpAllowed()}>
              <${OtpOption} />
            <//>
            <${SellingPlanOption} />
            <${Show} when=${() => !otpIsFirst() && otpAllowed()}>
              <${OtpOption} />
            <//>
          </div>
        <//>
      <//>
    <//>
  `;
}

function PrivePricePerBite(props) {
  const [state, _] = usePrive();
  const [product] = useProduct();

  const variant = () =>
    product().variants.find((v) => `${parseId(v.id)}` === `${props.variantId}`);

  const pricePerBite = createMemo(() => {
    const v = variant();
    const period = extractPeriod(v.title);
    const amount = parseInt(v.title);
    const sellingPlans = state.sellingPlan.plans;

    const sellingPlan = getSelectedSellingPlanByVariant({
      variant: v,
      sellingPlans,
    });

    let price;

    if (sellingPlan && state.purchaseOption === "sub") {
      price = calcSellingPlanPrice({ sellingPlan, variant: v });
    } else {
      price = v.price;
    }

    let multiplier = 1;

    if (period === "WEEK") {
      multiplier = 7;
    } else if (period === "MONTH") {
      multiplier = 30;
    }

    if (isNaN(amount)) return 0;

    return price / (amount * multiplier);
  });

  const formattedPrice = createMemo(() => Shopify.formatMoney(pricePerBite()));

  return html`
    <${Show} when=${() => pricePerBite() > 0}>
      ${() => `${formattedPrice()}/Bite`}
    <//>
  `;
}

function VariantSelector(props) {
  const [product, methods] = useProduct();

  function handleChange(e) {
    const variant = product().variants.find(
      (v) => parseId(v.id) === e.target.value,
    );
    methods.variant.update(variant);
    if (props.onChange) props.onChange(variant);
  }

  const isSelected = createSelector(() =>
    parseId(product()?.selectedVariant.id),
  );

  return html`
    <div class="block @container">
      <div class="flex gap-2 flex-row flex-wrap w-full items-stretch">
        <${For} each=${() => product().variants}>
          ${(variant) => {
            const variantId = parseId(variant.id);
            const id = `variantId-${variantId}`;
            return html`
              <div
                class="flex-shrink-0 flex-grow basis-1/3 @[10rem]:basis-full @xs:basis-1/3"
              >
                <input
                  id=${id}
                  form=${() => props.form || undefined}
                  type="radio"
                  name="id"
                  class="peer hidden variant-radio"
                  value=${variantId}
                  checked=${() => isSelected(variantId)}
                  disabled=${() => props.disabled}
                  onChange=${(e) => handleChange(e)}
                />
                <label
                  for=${id}
                  class="flex items-center justify-center py-2 px-4 w-full text-section-body font-bold border border-section-primary-button-background rounded-md font-body hover:cursor-pointer peer-checked:bg-section-accent-one peer-checked:text-section-heading-one h-full relative flex flex-col gap-0.5 leading-none peer-disabled:opacity-60 peer-disabled:cursor-not-allowed"
                >
                  <span>
                    <${Switch}>
                      <${Match} when=${() => variant?.shortTitle?.value}>
                        ${() => variant.shortTitle.value}
                      <//>
                      <${Match} when=${() => !variant?.shortTitle?.value}>
                        ${() => variant.title}
                      <//>
                    <//>
                  </span>
                  <span
                    class="text-[10px] text-section-body/60 italic font-semibold"
                  >
                    <${PrivePricePerBite} variantId=${variantId} />
                  </span>
                </label>
              </div>
            `;
          }}
        <//>
      </div>
    </div>
  `;
}

function RecommendedProduct() {
  const [product, methods] = useProduct();
  const featuredImage = () => {
    const p = product();
    if (!p.recommenedImage?.value) return p.featuredImage;
    return p.recommenedImage.data.details;
  };

  const [status, setStatus] = createSignal("default");

  const productOOS = () => {
    const p = product();
    if (!p.oosMessaging) return false;

    return p.oosMessaging.value;
  };

  const ingredients = useIngredientsByProduct(product);

  const inCartItem = createMemo(() => {
    const p = product();
    return StorefrontCart.cart().items.find(
      (item) => item.product_id === parseId(p.id),
    );
  });

  const productInCart = createMemo(() => !!inCartItem());

  const [buttonProps, setButtonProps] = createSignal({
    innerHTML: "Add to cart",
    disabled: false,
  });

  const [inCartButtonProps, setInCartButtonProps] = createSignal({
    innerHTML: `<i class="far fa-check"></i> In cart`,
    disabled: false,
  });

  let requestTimeoutId = null;

  let form = null;

  async function handleUpdate(fn) {
    if (requestTimeoutId) clearTimeout(requestTimeoutId);

    const formData = new FormData(form),
      variantId = formData.get("id"),
      sellingPlanId = formData.get("selling_plan");

    batch(() => {
      setInCartButtonProps({
        innerHTML: "Updating...",
        disabled: true,
      });
      setStatus("loading");
    });

    try {
      await StorefrontCart.updateItem({
        id: inCartItem()?.key,
        merchandiseId: variantId,
        sellingPlanId: sellingPlanId || null,
      });
      batch(() => {
        setInCartButtonProps({
          innerHTML: "Updated",
          disabled: true,
        });
        setStatus("success");
      });
    } catch (error) {
      console.log(error);
      if (error instanceof Error) {
        batch(() => {
          setInCartButtonProps({
            innerHTML: "Failed to update",
            disabled: true,
          });
          setStatus("error");
        });
      }
    } finally {
      requestTimeoutId = setTimeout(() => {
        batch(() => {
          setInCartButtonProps({
            innerHTML: `<i class="far fa-check"></i> In cart`,
            disabled: false,
          });
          setStatus("default");
        });
      }, 1000);
    }
  }

  async function addToCart(e) {
    e.preventDefault();
    if (requestTimeoutId) clearTimeout(requestTimeoutId);

    const formData = new FormData(e.target);

    batch(() => {
      setButtonProps((prev) => ({
        ...prev,
        innerHTML: "Adding...",
        disabled: true,
      }));
      setStatus("loading");
    });

    try {
      await StorefrontCart.addItem({
        merchandiseId: formData.get("id"),
        quantity: 1,
        sellingPlanId: formData.get("selling_plan") || null,
      });
      batch(() => {
        setButtonProps({
          innerHTML: "Added",
          disabled: true,
        });
        setStatus("success");
      });
    } catch (error) {
      if (error instanceof Error) {
        batch(() => {
          setButtonProps({
            innerHTML: "Failed!",
            disabled: true,
          });
          setStatus("error");
        });
      }
    } finally {
      requestTimeoutId = setTimeout(() => {
        batch(() => {
          setButtonProps((prev) => ({
            ...prev,
            innerHTML: "Add to cart",
            disabled: false,
          }));
          setStatus("default");
        });
      }, 1000);
    }
  }

  async function removeFromCart(e) {
    e.preventDefault();
    if (requestTimeoutId) clearTimeout(requestTimeoutId);

    batch(() => {
      setInCartButtonProps((prev) => ({
        ...prev,
        innerHTML: "Removing...",
        disabled: true,
      }));
      setStatus("loading");
    });
    try {
      await StorefrontCart.removeItem(inCartItem()?.key);
      batch(() => {
        setInCartButtonProps({
          innerHTML: "Removed",
          disabled: true,
        });
        setStatus("success");
      });
    } catch (error) {
      if (error instanceof Error) {
        batch(() => {
          setInCartButtonProps({
            innerHTML: "Failed...",
            disabled: true,
          });
          setStatus("error");
        });
      }
    } finally {
      requestTimeoutId = setTimeout(() => {
        batch(() => {
          setInCartButtonProps((prev) => ({
            ...prev,
            innerHTML: `<i class="far fa-check"></i> In cart`,
            disabled: false,
          }));
          setStatus("default");
        });
      }, 1000);
    }
  }

  const baseButtonClasses =
      "text-base font-body font-bold rounded-full button text-section-body w-full bg-[#EBE9CA] text-center normal-case",
    inCartButtonClasses =
      "text-base font-body font-bold rounded-full button text-section-body w-full bg-[#EFFFC2] text-center normal-case";

  const formId = createUniqueId();

  return html`
    <div
      class="bg-section-background rounded-lg overflow-hidden text-left col-span-1"
    >
      <div class="w-full h-[157px]">
        <${Show} when=${featuredImage}>
          <img
            class="object-cover w-full h-full"
            src=${() => featuredImage().src}
            alt=${() => featuredImage().alt}
          />
        <//>
      </div>
      <${Show} when=${productOOS}>
        <div class="bg-section-accent-one p-1 border-y border-section-body/40">
          <p class="text-xs font-bold text-center mb-0">
            ${() => productOOS()}
          </p>
        </div>
      <//>
      <div class="px-6 py-4">
        <h3 class="text-3xl leading-none">
          ${() => product()?.shortenedTitle?.value || product().title}
        </h3>
        <p class="leading-[1.15] mb-2">
          The ${() => product()?.shortenedTitle?.value || product().title} works
          for you because you care about ${" "}
          <${ProductGoals} product=${product} />.
        </p>
        <hr class="mb-4" />
        <h4 class="mb-4 text-xs font-bold leading-tight uppercase font-body">
          Key Ingredients
        </h4>
        <div class="grid grid-cols-5 gap-4 mb-4">
          <${For} each=${ingredients}>
            ${(ingredient) => html`
              <${Show} when=${() => ingredient.image?.data}>
                <div class="col-span-1 flex flex-col items-center gap-1">
                  <div
                    class="overflow-hidden rounded-full aspect-1 bg-[rgba(var(--color-eggshell))]"
                  >
                    <img
                      class="object-cover w-full h-full"
                      src=${() => ingredient.image?.data?.details?.square}
                      alt=${() => ingredient.image?.data?.details?.alt}
                    />
                  </div>
                  <p class="text-xs text-center mb-0">
                    ${() => ingredient.name.value}
                  </p>
                </div>
              <//>
            `}
          <//>
        </div>
        <hr class="mt-2 mb-4" />
        <form
          id=${formId}
          class="flex flex-col gap-4"
          onSubmit=${(e) =>
            !productInCart() ? addToCart(e) : removeFromCart(e)}
          ref=${(el) => (form = el)}
        >
          <div class="flex flex-col gap-2">
            <${VariantSelector}
              disabled=${() => status() !== "default"}
              form=${formId}
            />
            <${PriveVariantWidget}
              disabled=${() => status() !== "default"}
              form=${formId}
              onChange=${(_) => productInCart() && handleUpdate()}
            />
          </div>
          <${Switch}>
            <${Match} when=${() => !productInCart()}>
              <button class=${baseButtonClasses} ...${() => buttonProps()} />
            <//>
            <${Match} when=${() => productInCart()}>
              <button
                class=${inCartButtonClasses}
                ...${() => inCartButtonProps()}
              />
            <//>
          <//>
        </form>
      </div>
    </div>
  `;
}

function DailyLineup() {
  const results = useResults();
  const meta = useMeta();

  const name = () =>
    results()?.original?.answers?.basics?.first_name?.values?.at(0)?.value;

  const config = {
    mode: "free-snap",
    loop: false,
    slides: {
      perView: "auto",
      perScroll: 1,
      spacing: 16,
    },
    breakpoints: {
      "(min-width: 768px)": {
        center: true,
        mode: "free-snap",
        loop: false,
        slides: {
          perView: "auto",
          perScroll: 1,
          spacing: 16,
        },
      },
    },
  };

  return html`
    <div
      class="flex justify-center w-full pt-24 pb-4 tablet:py-24 text-center bg-[rgba(var(--color-eggshell))] overflow-hidden"
    >
      <div class="container flex-1">
        <${Show} when=${meta.logo_large}>
          <a
            href="/"
            onClick=${(e) => {
              e.preventDefault();
              window.location.href = "/";
            }}
            class="flex justify-center items-center mb-8"
            innerHTML=${meta.logo_large}
          />
        <//>
        <h2 class="mb-6 text-6xl">Hi ${name}, here's your GEM daily lineup</h2>
        <div class="max-w-[365px] mx-auto">
          <p class="mb-8 text-base">
            We made our selections for you based on your focus on ${AllGoals}
            <br />
            We also considered that ${LifestyleCopy}
          </p>
          <p class="mb-0 text-base"><strong>Let's dive in</strong></p>
          <p><i class="fal fa-long-arrow-down animate-bounce"></i></p>
        </div>

        <keen-slider
          class="keen-slider tablet:!overflow-hidden !overflow-visible"
          center-slides="true"
          config=${config}
        >
          <${For} each=${() => results()?.recommendedProducts}>
            ${(product) => html`
              <${ProductProvider} product=${() => product}>
                <div
                  class="keen-slider__slide"
                  style="min-width:380px;max-width:380px"
                >
                  <${RecommendedProduct} product=${product} />
                </div>
              <//>
            `}
          <//>
        </keen-slider>
        <div class="max-w-[675px] mx-auto">
          <hr class="mb-4 mt-10 border-section-body" />
          <p class="mb-0 text-base"><strong>Eat one daily</strong></p>
          <br />
          <p class="mb-0 text-sm hidden">
            <em
              >For Vegan, Gluten Free, Plant-based in Basics please note that we
              are vegan, gluten free in our product on the results page somwhere
              at the bottom</em
            >
          </p>
        </div>
      </div>
    </div>
  `;
}

function KeyIngredients() {
  const results = useResults();

  return html`
    <div class="max-w-[365px] mx-auto">
      <h2 class="text-6xl">Your key ingredients</h2>
      <p class="mb-8 text-base">
        We selected these natural whole-food ingredients to help you address
        your concerns with <${AllGoals} />.
      </p>
    </div>
    <div class="grid gap-4 mb-24 tablet:grid-cols-3">
      <${For} each=${() => results()?.populated}>
        ${(outcome, index) => html`
          <div class="col-span-1">
            <div
              class="p-4 text-left rounded-md bg-[rgba(var(--color-eggshell))]"
            >
              <p class="mb-0 text-sm font-body">
                <span class="text-xs leading-tight uppercase">
                  We recommend: ${" "}
                </span>
                <strong>
                  ${() =>
                    replaceLastComma(
                      outcome.ingredients
                        .map((ingredient) => ingredient.name.value)
                        .join(", "),
                      " +",
                    )}
                </strong>
              </p>
              <p class="mb-4 text-sm font-body">
                <span class="text-xs leading-tight uppercase"
                  >Found in:${" "}</span
                >
                <strong>
                  ${() =>
                    outcome.product?.shortenedTitle?.value ||
                    outcome.product.title}
                </strong>
              </p>
              <hr class="mb-4" />
              <div class="grid grid-cols-4 gap-6 mb-4">
                <div class="col-span-1 flex items-center justify-center">
                  <div class="overflow-hidden rounded-md aspect-1 flex">
                    <${For} each=${() => outcome.ingredients}>
                      ${(ingredient) => html`
                        <${Show} when=${() => ingredient.image?.data}>
                          <img
                            class="object-cover w-full h-full"
                            style=${() =>
                              `width: calc(100% / ${outcome.ingredients.length})`}
                            src=${() => ingredient.image.data.details.square}
                            alt=${() => ingredient.image.data.details.alt}
                          />
                        <//>
                      `}
                    <//>
                  </div>
                </div>
                <div class="col-span-3 h-full flex items-center">
                  <p class="mb-0 text-sm">${() => outcome.ingredientCopy}</p>
                </div>
              </div>
              <modal-trigger
                action="open"
                target=${() => `#modal-${index()}`}
                on="click"
              >
                <p
                  class="underline underline-offset-4 text-sm cursor-pointer mb-0"
                >
                  Learn more
                </p>
              </modal-trigger>
              ${() => html`
                <${IngredientsModal}
                  ingredients=${() => outcome.ingredients}
                  id=${() => `modal-${index()}`}
                />
              `}
            </div>
          </div>
        `}
      <//>
    </div>
  `;
}

function WhatToExpect() {
  return html`
    <h2 class="mb-8 text-6xl">Here's what to expect</h2>
    <div class="max-w-[670px] mx-auto">
      <div class="mb-8 overflow-hidden rounded-md aspect-1">
        <img
          class="object-contain w-full h-full"
          src="https://cdn.shopify.com/s/files/1/0002/5673/7286/files/Unboxing.png?v=1741217399"
          alt=""
        />
      </div>
    </div>
    <div class="mx-auto text-left max-w-[475px]">
      <ul class="what-to-expect__list">
        <li>
          <strong>Free</strong> Welcome-Kit with Home Storage Container & Mesh
          Bag
        </li>
        <li><strong>Flexible subscription:</strong> pause or cancel anytime</li>
        <li>
          <strong>Free gifts:</strong> a chance to receive a free bonus item.
        </li>
        <li>Delivered every 28 days</li>
        <li>Satisfaction guaranteed</li>
      </ul>
    </div>
  `;
}

function Container(props) {
  return html`
    <div
      class="flex justify-center w-full pt-8 pb-24 tablet:py-24 text-center overflow-hidden"
    >
      <div class="container">${() => props.children}</div>
    </div>
  `;
}

function OtherProduct() {
  const [product, methods] = useProduct();
  const [status, setStatus] = createSignal("default");

  const productOOS = () => {
    const p = product();
    if (!p.oosMessaging) return false;

    return p.oosMessaging.value;
  };

  const inCartItem = createMemo(() => {
    const p = product();
    return StorefrontCart.cart().items.find(
      (item) => item.product_id === parseId(p.id),
    );
  });

  const productInCart = createMemo(() => !!inCartItem());

  const [buttonProps, setButtonProps] = createSignal({
    innerHTML: "Add to cart",
    disabled: false,
  });

  const [inCartButtonProps, setInCartButtonProps] = createSignal({
    innerHTML: `<i class="far fa-check"></i> In cart`,
    disabled: false,
  });

  function onVariantChange(variantId) {
    const variant = product().variants?.find(
      (v) => parseId(v.id) === variantId,
    );
    methods.variant.update(variant);
  }

  let requestTimeoutId = null;

  let form = null;

  async function handleUpdate() {
    if (requestTimeoutId) clearTimeout(requestTimeoutId);

    const formData = new FormData(form),
      variantId = formData.get("id"),
      sellingPlanId = formData.get("selling_plan");

    batch(() => {
      setInCartButtonProps({
        innerHTML: "Updating...",
        disabled: true,
      });
      setStatus("loading");
    });

    const variant = product().variants?.find(
      (v) => parseId(v.id) === variantId,
    );

    try {
      await StorefrontCart.updateItem({
        id: inCartItem()?.key,
        merchandiseId: variantId,
        sellingPlanId: sellingPlanId || null,
      });
      batch(() => {
        setInCartButtonProps({
          innerHTML: "Updated",
          disabled: true,
        });
        setStatus("success");
      });
    } catch (error) {
      console.log(error);
      if (error instanceof Error) {
        batch(() => {
          setInCartButtonProps({
            innerHTML: "Failed to update",
            disabled: true,
          });
          setStatus("error");
        });
      }
    } finally {
      requestTimeoutId = setTimeout(() => {
        batch(() => {
          setInCartButtonProps({
            innerHTML: `<i class="far fa-check"></i> In cart`,
            disabled: false,
          });
          setStatus("default");
        });
      }, 1000);
    }
  }

  async function addToCart(e) {
    e.preventDefault();
    if (requestTimeoutId) clearTimeout(requestTimeoutId);

    const formData = new FormData(e.target);

    batch(() => {
      setButtonProps((prev) => ({
        ...prev,
        innerHTML: "Adding...",
        disabled: true,
      }));
      setStatus("loading");
    });

    try {
      await StorefrontCart.addItem({
        merchandiseId: formData.get("id"),
        quantity: 1,
        sellingPlanId: formData.get("selling_plan") || null,
      });
      batch(() => {
        setButtonProps({
          innerHTML: "Added",
          disabled: true,
        });
        setStatus("success");
      });
    } catch (error) {
      if (error instanceof Error) {
        batch(() => {
          setButtonProps({
            innerHTML: "Failed!",
            disabled: true,
          });
          setStatus("error");
        });
      }
    } finally {
      requestTimeoutId = setTimeout(() => {
        batch(() => {
          setButtonProps((prev) => ({
            ...prev,
            innerHTML: "Add to cart",
            disabled: false,
          }));
          setStatus("default");
        });
      }, 1000);
    }
  }

  async function removeFromCart(e) {
    e.preventDefault();
    if (requestTimeoutId) clearTimeout(requestTimeoutId);

    batch(() => {
      setInCartButtonProps((prev) => ({
        ...prev,
        innerHTML: "Removing...",
        disabled: true,
      }));
      setStatus("loading");
    });
    try {
      await StorefrontCart.removeItem(inCartItem()?.key);
      batch(() => {
        setInCartButtonProps({
          innerHTML: "Removed",
          disabled: true,
        });
        setStatus("success");
      });
    } catch (error) {
      if (error instanceof Error) {
        batch(() => {
          setInCartButtonProps({
            innerHTML: "Failed...",
            disabled: true,
          });
          setStatus("error");
        });
      }
    } finally {
      requestTimeoutId = setTimeout(() => {
        batch(() => {
          setInCartButtonProps((prev) => ({
            ...prev,
            innerHTML: `<i class="far fa-check"></i> In cart`,
            disabled: false,
          }));
          setStatus("default");
        });
      }, 1000);
    }
  }

  const baseButtonClasses =
      "text-base font-body font-bold rounded-full button text-section-body w-full bg-[#EBE9CA] text-center normal-case",
    inCartButtonClasses =
      "text-base font-body font-bold rounded-full button text-section-body w-full bg-[#EFFFC2] text-center normal-case";

  const formId = createUniqueId();

  return html`
    <${Show}
      when=${productOOS}
      fallback=${() => () => html`
        <div class="mb-8 overflow-hidden rounded-md aspect-1">
          <${Show} when=${() => product()?.featuredImage}>
            <img
              class="object-contain w-full h-full bg-[rgba(var(--color-eggshell))]"
              src=${() => product().featuredImage.src}
              alt=${() => product().featuredImage.alt}
            />
          <//>
        </div>
      `}
    >
      <div class="rounded-md rounded-b-none aspect-1">
        <${Show} when=${() => product()?.featuredImage}>
          <img
            class="object-contain w-full h-full bg-[rgba(var(--color-eggshell))]"
            src=${() => product().featuredImage.src}
            alt=${() => product().featuredImage.alt}
          />
        <//>
      </div>
      <div
        class="bg-section-accent-one p-1 rounded-b-xl border-t border-x border-b border-section-body/40 mb-8 relative z-10"
      >
        <p class="text-xs font-bold text-center mb-0">${() => productOOS()}</p>
      </div>
    <//>

    <h3 class="mb-3 text-4xl">
      ${() => product()?.shortenedTitle?.value || product()?.title}
    </h3>
    <p class="mb-3 text-body">${() => product()?.description}</p>
    <p class="text-body">
      <a href=${() => product()?.url} class="underline underline-offset-4"
        >Learn more</a
      >
    </p>
    <form
      id=${formId}
      ref=${(el) => (form = el)}
      class="flex flex-col gap-4"
      onSubmit=${(e) => (productInCart() ? removeFromCart(e) : addToCart(e))}
    >
      <${VariantSelector}
        disabled=${() => status() !== "default"}
        form=${formId}
      />
      <${PriveVariantWidget}
        onChange=${(_) => productInCart() && handleUpdate()}
        disabled=${() => status() !== "default"}
        form=${formId}
      />
      <${Switch}>
        <${Match} when=${() => !productInCart()}>
          <button class=${baseButtonClasses} ...${() => buttonProps()} />
        <//>
        <${Match} when=${() => productInCart()}>
          <button
            class=${inCartButtonClasses}
            ...${() => inCartButtonProps()}
          />
        <//>
      <//>
    </form>
  `;
}

function RoundOutRoutine() {
  const results = useResults();

  const config = {
    mode: "free-snap",
    loop: false,
    slides: {
      perView: "auto",
      perScroll: 1,
      spacing: 16,
    },
    breakpoints: {
      "(min-width: 768px)": {
        center: true,
        mode: "free-snap",
        loop: false,
        slides: {
          perView: "auto",
          perScroll: 1,
          spacing: 16,
        },
      },
    },
  };
  return html`
    <${Show} when=${() => results()?.otherProducts?.length > 0}>
      <h2 class="mb-8 text-6xl">Round out your routine</h2>
      <div class="mb-24">
        <keen-slider
          class="keen-slider tablet:!overflow-hidden !overflow-visible"
          center-slides="true"
          config=${config}
        >
          <${For} each=${() => results()?.otherProducts}>
            ${(product) => html`
              <${ProductProvider} product=${() => product}>
                <div
                  class="keen-slider__slide"
                  style="min-width:330px;max-width:330px"
                >
                  <${OtherProduct} />
                </div>
              <//>
            `}
          <//>
        </keen-slider>
      </div>
    <//>
  `;
}

function CartNavigation() {
  const meta = useMeta();
  const cart = () => StorefrontCart.cart();

  const hasItems = () => cart().item_count > 0;

  const hasDiscount = () =>
    cart().original_total_price > cart().discounted_subtotal;

  const formattedTotal = () => Shopify.formatMoney(cart().discounted_subtotal);

  const formattedOriginal = () =>
    Shopify.formatMoney(cart().original_total_price);

  const discountPercentage = () =>
    Math.round(
      (1 - cart().discounted_subtotal / cart().original_total_price) * 100,
    );

  return html`
    <div class="bg-section-accent-one py-4 fixed bottom-0 left-0 right-0">
      <div class="container">
        <div class="flex justify-between items-center gap-1">
          <div class="text-left flex-shrink-0 flex-grow-0 basis-auto">
            <a
              href=${meta.quizUrl}
              onClick=${(e) => {
                e.preventDefault();
                return (window.location.href = meta.quizUrl);
              }}
              class="text-xl button button--primary button--sm"
            >
              <i class="fa-regular fa-arrow-left-long"></i>
            </a>
          </div>

          <div class="flex items-center justify-center flex-1">
            <${Show} when=${hasItems}>
              <p class="mb-0 text-base flex gap-1">
                <strong>Cart:</strong>
                <span>
                  <${Show} when=${hasDiscount}>
                    <s class="opacity-50"> ${formattedOriginal}${" "} </s>
                  <//>
                  <strong> ${formattedTotal} </strong>
                </span>

                <${Show} when=${hasDiscount}>
                  <span class="text-xs bg-[#FFFEF0] uppercase rounded-md p-1">
                    ${discountPercentage}% Off
                  </span>
                <//>
              </p>
            <//>
          </div>

          <div class="text-right flex-shrink-0 flex-grow-0 basis-auto">
            <button
              class="text-xl button button--primary button--sm"
              disabled=${() => !hasItems()}
              onClick=${(e) => {
                if (!hasItems()) return;
                return (window.location.href = cart().checkoutUrl);
              }}
            >
              <i class="fa-regular fa-arrow-right-long"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  `;
}

function ResultsPage() {
  const results = useResults();

  createEffect(() => {
    const r = results();
    if (!r) return;
    if (typeof window.klaviyo === "undefined") return;

    const answers = extractAnswers(r.original.answers);
    const email = r.original.answers.email;
    const firstName = answers
      .find((a) => a.values.find((v) => v.label === "First Name"))
      ?.values?.at(0)?.value;
    let identifyProperties = {};
    if (email) identifyProperties.email = email;
    if (firstName) identifyProperties.first_name = firstName;
    const customAttributePrefix = "2025 Quiz: ";
    answers.forEach((answer) => {
      if (answer.values.some((v) => v.label === "First Name")) return;
      identifyProperties[`${customAttributePrefix}${answer.question}`] =
        answer.values.map((v) => v.label).join(", ");
    });
    klaviyo.identify(identifyProperties);
    if (!email) return;
    const url = "https://a.klaviyo.com/client/subscriptions?company_id=JsAqRX";
    const options = {
      method: "POST",
      headers: {
        accept: "application/vnd.api+json",
        revision: "2025-01-15",
        "content-type": "application/vnd.api+json",
      },
      body: JSON.stringify({
        data: {
          type: "subscription",
          attributes: {
            profile: {
              data: {
                type: "profile",
                attributes: {
                  email,
                  first_name: firstName || undefined,
                  subscriptions: {
                    email: {
                      marketing: {
                        consent: "SUBSCRIBED",
                      },
                    },
                  },
                },
              },
            },
          },
          relationships: {
            list: {
              data: {
                type: "list",
                id: "SvSdq3",
              },
            },
          },
        },
      }),
    };
    fetch(url, options);
  });

  const hasResults = createMemo(() => {
    const r = results();
    return typeof r !== "undefined" && !StorefrontCart.isLoading;
  });

  return html`
    <${Show} when=${hasResults} fallback=${() => LoadingResults}>
      <${DailyLineup} />
      <${Container}>
        <${KeyIngredients} />
        <${RoundOutRoutine} />
        <${WhatToExpect} />
      <//>
      <${CartNavigation} />
    <//>
  `;
}

function RootLayout(props) {
  return html`
    <${Suspense} fallback=${() => LoadingResults}>
      <${MetaProvider}>
        <${ResultsProvider}>
          <cart-context> ${() => props.children} </cart-context>
        <//>
      <//>
    <//>
  `;
}

const routes = [
  {
    path: "/",
    component: ResultsPage,
  },
];

function App() {
  return html` <${HashRouter} root=${() => RootLayout}> ${routes} <//> `;
}

render(() => html`<${App} />`, document.getElementById("app"));